/**
 * The raid tour (v1 ticket 52), when it is offered, DOM-free: one localStorage flag per device (`li-raid-tour`,
 * `done` or `skipped`), whether a first tap on Raid or Defend goes through the tour first (`tourDue`,
 * `entryPath`), and which `next` paths the tour hands on to (`safeNext`).
 */
import type { RaidOverview } from './raid-api';

export const TOUR_KEY = 'li-raid-tour';
export type TourFlag = 'done' | 'skipped' | null;

/** The flag on this device; null when unset or storage is out of reach. */
export function readTourFlag(store: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): TourFlag {
  try {
    const v = store?.getItem(TOUR_KEY);
    return v === 'done' || v === 'skipped' ? v : null;
  } catch {
    return null;
  }
}

export function writeTourFlag(flag: 'done' | 'skipped', store: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage): void {
  try {
    store?.setItem(TOUR_KEY, flag);
  } catch {
    /* private mode or full storage: the tour simply comes again */
  }
}

/**
 * The tour is due while this device never finished or skipped it and the learner has never raided: no report, no
 * raid of their own on its way and no draft. An army at the gates alone still counts (a first defence).
 */
export function tourDue(o: Pick<RaidOverview, 'reports' | 'outgoing' | 'draft'> | undefined, flag: TourFlag): boolean {
  if (flag) return false;
  if (!o) return false;
  return !o.reports.length && !o.outgoing.length && !o.draft;
}

/** Where a first tap goes: the tour, handing on to `path`, when it is due; else `path` itself. */
export function entryPath(o: Pick<RaidOverview, 'reports' | 'outgoing' | 'draft'> | undefined, path: string, flag: TourFlag = readTourFlag()): string {
  return tourDue(o, flag) ? `/raid/tour?next=${encodeURIComponent(path)}` : path;
}

/**
 * A `next` the tour may go on to: a same-origin path under `/raid/` (never the tour itself, no dot-segment even
 * percent-encoded, no encoded slash or backslash) or exactly `/circle`.
 */
export function safeNext(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  if (/[\u0000-\u001f]/.test(raw)) return null;
  const path = raw.split(/[?#]/)[0];
  if (path === '/circle') return raw === '/circle' ? raw : null;
  if (!path.startsWith('/raid/') || path.length <= '/raid/'.length) return null;
  if (path === '/raid/tour' || path.startsWith('/raid/tour/')) return null;
  // An encoded slash or backslash, or a dot-segment however it is encoded (`%2e%2e`), would resolve elsewhere.
  if (/%2f|%5c/i.test(path)) return null;
  for (const seg of path.split('/')) {
    let s: string;
    try { s = decodeURIComponent(seg); } catch { return null; }
    if (s === '..' || s === '.') return null;
  }
  return raw;
}
